import { useRef, useEffect } from 'react';
import Scanlines from './Scanlines';

/**
 * Configuration interface for the glitchy checkerboard animation
 */
interface CheckerboardConfig {
  /** Size of each square tile in the checkerboard pattern */
  tileSize: number;
  /** Probability of a glitch effect occurring per frame (0.0 - 1.0) */
  glitchChance: number;
  /** Probability of an entire block glitching together (0.0 - 1.0) */
  glitchBlockChance: number;
  /** Maximum pixel displacement for glitch effects */
  glitchDisplacement: number;
  /** Primary base color for checkerboard tiles (hex string) */
  baseColor1: string;
  /** Secondary base color for checkerboard tiles (hex string) */
  baseColor2: string;
  /** Array of colors used for glitch effects (hex strings) */
  glitchColors: string[];
}

/**
 * Animated glitchy checkerboard background component with scanlines overlay
 * 
 * Creates a full-screen canvas background with:
 * - Animated checkerboard pattern with configurable tile size
 * - Random glitch effects with displacement and color changes
 * - Block-based glitching for more dramatic effects
 * - Overlay scanlines component for additional visual depth
 * - Responsive canvas that adjusts to window size
 * - Optimized rendering using requestAnimationFrame
 * 
 * @returns JSX element containing the animated background with scanlines overlay
 */
const GlitchyCheckerboard = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /** Configuration object for checkerboard animation behavior */
  const config: CheckerboardConfig = {
    tileSize: 2, // Size of each square in the checkerboard
    glitchChance: 0.05, // Probability of a glitch effect occurring per frame
    glitchBlockChance: 0.1, // Probability of a whole block glitching
    glitchDisplacement: 5, // Max pixel displacement for glitches
    baseColor1: '#111111', // Darker gray for subtlety
    baseColor2: '#333435', // Slightly lighter gray for subtle contrast
    glitchColors: ['#2a2628', '#282a2c', '#262a2a', '#2a2626', '#262a28'], // Very dark static-like glitch colors
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationFrameId: number;

    /**
     * Handle canvas resizing to match window dimensions
     * Ensures the background covers the full viewport
     */
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    /**
     * Main drawing function called on every animation frame
     * Renders the checkerboard pattern with glitch effects
     */
    const draw = () => {
      const { width, height } = canvas;
      const { tileSize, glitchChance, glitchBlockChance, glitchDisplacement, baseColor1, baseColor2, glitchColors } = config;
      
      // Clear the canvas for the new frame
      ctx.clearRect(0, 0, width, height);

      // Calculate number of columns and rows
      const cols = Math.ceil(width / tileSize);
      const rows = Math.ceil(height / tileSize);

      // Loop through each tile in the grid
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          
          let currentX = x * tileSize;
          let currentY = y * tileSize;
          let currentTileSize = tileSize;

          // Determine the base color for the checkerboard pattern
          ctx.fillStyle = (x + y) % 2 === 0 ? baseColor1 : baseColor2;

          // --- GLITCH EFFECT LOGIC ---
          if (Math.random() < glitchBlockChance) {
            // Glitch a whole block of tiles
            if (Math.random() < glitchChance) {
              const glitchWidth = (Math.floor(Math.random() * 4) + 1) * tileSize;
              const glitchHeight = (Math.floor(Math.random() * 4) + 1) * tileSize;
              ctx.fillStyle = glitchColors[Math.floor(Math.random() * glitchColors.length)];
              ctx.fillRect(currentX, currentY, glitchWidth, glitchHeight);
              // Skip the tiles that were just covered by the glitch block
              x += Math.floor(glitchWidth / tileSize) - 1; 
              continue;
            }
          } else {
             // Glitch individual tiles
            if (Math.random() < glitchChance) {
                // Displace the tile
                currentX += (Math.random() - 0.5) * glitchDisplacement;
                currentY += (Math.random() - 0.5) * glitchDisplacement;
                
                // Change its color
                ctx.fillStyle = glitchColors[Math.floor(Math.random() * glitchColors.length)];
                
                // Slightly alter size
                currentTileSize = tileSize * (Math.random() * 0.5 + 0.75);
            }
          }
          
          ctx.fillRect(currentX, currentY, currentTileSize, currentTileSize);
        }
      }
    };

    // The animation loop
    const animate = () => {
      draw();
      animationFrameId = requestAnimationFrame(animate);
    };

    // Initial setup and start
    handleResize();
    animate();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup function to be called when the component unmounts
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [config]); // Rerun effect if config changes

  return (
    <div className="absolute top-0 left-0 w-full h-full">
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full z-0"
        aria-hidden="true"
      />
      <Scanlines />
    </div>
  );
};

export default GlitchyCheckerboard;
